import React, { useState, useEffect, useMemo } from 'react';
import { Table, TableBody, TableCell, TableRow, TableHead, IconButton } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { makeStyles } from '@mui/styles';
import CentralCalls from '../../centralCalls';
import { faPrint } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { set } from 'lodash';
import { parseISO, differenceInCalendarDays, format } from 'date-fns';

const useStyles = makeStyles((theme) => ({
  '@global': {
    '@media print': {
      'body *': { visibility: 'hidden' },
      '#ticket-print-root, #ticket-print-root *': { visibility: 'visible' },
      '#ticket-print-root': { position: 'absolute', left: 0, top: 0, right: 0 },
    },
  },
  fullScreen: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    zIndex: 9999,
    backgroundColor: 'white',
    overflow: 'auto',
    margin: 0,
    padding: 0
  },
  headerBar: {
    padding: '10px 15px',
    borderBottom: '1px solid #ccc',
    marginBottom: 0
  },
  titleWrap: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  table: {
    width: 'calc(100% - 20px)',
    tableLayout: 'fixed',
    margin: '0px auto',
    borderCollapse: 'collapse'
  },
  tableHeaderCell: {
    backgroundColor: '#f4f4f4',
    fontWeight: 'bold',
    borderBottom: '1px solid #ddd',
    borderRight: '1px solid #e0e0e0',
    borderLeft: '1px solid #e0e0e0',
    whiteSpace: 'normal',
    padding: '0px 0px',
  },
  tableCell: {
    borderRight: '1px solid #e0e0e0',
    borderLeft: '1px solid #e0e0e0',
    whiteSpace: 'normal',
    padding: '0px 0px',
    verticalAlign: 'top'
  },
  row: {
    borderTop: '1px solid #e0e0e0',
    borderLeft: '1px solid #e0e0e0',
  },
  groupHeaderRow: {
    backgroundColor: '#d0d0d0',
    borderTop: '2px solid #999'
  },
  groupHeaderText: {
    fontWeight: 700,
    fontSize: '0.95rem'
  },
  notesEmphasis: { fontWeight: 700 },
  screenOnly: {},
  '@media print': {
    '@page': { size: 'letter portrait', margin: '0.25in' },
    fullScreen: { position: 'static', overflow: 'visible', padding: 0, margin: 0 },
    screenOnly: { display: 'none !important' },
    headerBar: { padding: '5px 10px', borderBottom: 'none', marginBottom: '4px' },
    table: { width: '100%', margin: 0, tableLayout: 'fixed', borderCollapse: 'collapse' },
    tableHeaderCell: {
      fontSize: '10px',
      padding: '3px 5px',
      borderBottom: '1px solid #000',
      borderRight: '1px solid #000',
      borderLeft: '1px solid #000'
    },
    tableCell: {
      fontSize: '8.5px',
      padding: '2px 4px',
      borderRight: '1px solid #000',
      borderLeft: '1px solid #000'
    },
    row: { breakInside: 'avoid', pageBreakInside: 'avoid' },
    thead: { display: 'table-header-group' },
    tfoot: { display: 'table-footer-group' }
  }
}));

function TicketPrintOut() {
  const classes = useStyles();
  const [data, setData] = useState([]);
  const [isPieceWork, setIsPeiceWork] = useState(false);
  const [plumbers, setPlumbers] = useState('');
  const [teamName, setTeamName] = useState('');
  const [hourlyRate, setHourlyRate] = useState(null);
  const [insuranceRate, setInsuranceRate] = useState(null);
  const [glExpDate, setGlExpDate] = useState(null);
  const [wcExpDate, setWcExpDate] = useState(null);
  const handlePrint = () => window.print();

  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const team_oid = Number(query.get('team_oid'));
  const [totalCost, setTotalCost] = useState(0);
  const [totalHours, setTotalHours] = useState(0);

  // employee_oids parsing (supports "[5]", "[5,6]", or "5,6")
  const employeeParam = query.get('employee_oids');
  let employee_oids = [];
  if (employeeParam) {
    try {
      employee_oids = JSON.parse(employeeParam).map((id) => Number(id));
    } catch {
      employee_oids = employeeParam
        .split(',')
        .map((id) => Number(id.trim()))
        .filter((n) => !Number.isNaN(n));
    }
  }

  const startDate = query.get('start_date');
  const endDate = query.get('end_date');
  const employeeName = query.get('employee_name');

  const sortByArrayOrder = (list, arrayOrder) => {
    if (!Array.isArray(arrayOrder) || arrayOrder.length === 0) return list;
    const idx = new Map(arrayOrder.map((id, i) => [id, i]));
    const BIG = 1e9;
    return [...list].sort(
      (a, b) => (idx.get(a.phase_status_oid) ?? BIG) - (idx.get(b.phase_status_oid) ?? BIG)
    );
  };

  const renderDate = (val) => {
    if (!val) return '';
    try {
      const date = parseISO(val);
      return format(date, 'MM-dd-yyyy');
    } catch {
      return '';
    }
  };

  const formatCurrency = (val) => {
    if (val === null || val === undefined || val === '') return '';
    const num = Number(val);
    if (Number.isNaN(num)) return String(val);
    return num.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
  };

  const getPlumberNames = (row) => {
    // Try common fields your SPs might return. Fallback to header-level plumbers/teamName.
    return (
      row.plumbers ||
      row.plumber_names ||
      row.employee_names ||
      row.employees ||
      plumbers ||
      row.team_name ||
      teamName ||
      ''
    );
  };

  useEffect(() => {
    const fetchData = async () => {
      const isPiece = query.get('is_piece_work') === '1' ? true : false;
      setIsPeiceWork(isPiece);
      const sqlDate = CentralCalls.sqlFormatDate2(startDate);
      const proc = isPiece ? 'sp_schedulePieceWork' : 'sp_getOpenScheduleByDate';

      const [response, plumbersList, sortOrderRes] = await Promise.all([
        CentralCalls.callStoredProcedureWithParams(
          proc,
          {
            date: sqlDate,
            crews: [team_oid],
            isPastDue: 0,
            readyStat: ['open', 'scheduled'],
            employee_oids: employee_oids ? employee_oids : [],
            end_date: endDate,
            start_date: sqlDate,
          }
        ),
        CentralCalls.callStoredProcedureWithParams('sp_getPlumberNames', { team_oid: Number([team_oid]) }),
        CentralCalls.callStoredProcedureWithParams('sp_getCrewSortOrder', {
          job_date: startDate,
          employee_team_oid: team_oid,
          employee_oids: employee_oids ? employee_oids : [],
          is_piece_work: isPiece ? 1 : 0,
        }),
      ]);

      if (isPiece) {
        if (response && response.length > 0) {
          const team = response[0];
          //console.log(response[0]);
          //console.log('HERE', team.hourly_rate);
          //const name = employeeName + (team.hourly_rate != null ? " (*Hourly (" + team.hourly_rate + ") " : "");
          setHourlyRate(team.hourly_rate);
          setInsuranceRate(team.insurance_rate);
          setGlExpDate(team.gl_exp_date);
          setWcExpDate(team.wc_exp_date);
        }

        // compute totals for piece work using stored procedure (do not rely on URL param)
        try {
          const tot = await CentralCalls.callStoredProcedureWithParams('sp_calcPieceWorkTotal', {
            employee_oids: employee_oids ? employee_oids : [],
            start_date: CentralCalls.sqlFormatDate2(startDate),
            end_date: CentralCalls.sqlFormatDate2(endDate)
          });
          setTotalCost(tot?.[0]?.cost ? tot[0].cost : 0);
          setTotalHours(tot?.[0]?.hours ? tot[0].hours : 0);
        } catch (e) {
          console.error('Failed to fetch piece work totals', e);
          setTotalCost(0);
          setTotalHours(0);
        }

      } else {
        if (plumbersList?.[0]?.plumberList) {
          setPlumbers(plumbersList[0].plumberList.join(', '));
          setTeamName(plumbersList[0].teamName);
        }
      }

      let savedArrayOrder = null;
      try {
        const out = sortOrderRes?.[0].arrayOrder;
        savedArrayOrder = out;
      } catch {
        savedArrayOrder = null;
      }

      const rows = response ?? [];

      if (isPiece) {
        // Piece work view uses simple table; no custom ordering needed.
        setData(rows);
        return;
      }

      // Non-piece-work ordering logic (unchanged)
      const currentIds = rows.map((r) => r.phase_status_oid).filter(Boolean);
      if (!Array.isArray(savedArrayOrder) || savedArrayOrder?.length === 0) {
        await CentralCalls.callStoredProcedureWithParams('sp_upsertCrewSortOrder', {
          job_date: startDate,
          employee_team_oid: team_oid,
          arrayOrder: currentIds,
        });
        setData(rows);
        return;
      }

      const savedSet = new Set(savedArrayOrder);
      const merged = savedArrayOrder
        ? [
          ...savedArrayOrder.filter((id) => currentIds.includes(id)),
          ...currentIds.filter((id) => !savedSet.has(id)),
        ]
        : [];

      if (JSON.stringify(merged) !== JSON.stringify(savedArrayOrder)) {
        await CentralCalls.callStoredProcedureWithParams('sp_upsertCrewSortOrder', {
          job_date: startDate,
          employee_team_oid: team_oid,
          arrayOrder: merged,
        });
      }

      setData(sortByArrayOrder(rows, merged));
    };

    if (startDate) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [team_oid, startDate]);

  // Grouping for non-piece-work only
  const segments = useMemo(() => {
    if (!data || data.length === 0) return [];
    if (isPieceWork) return []; // not used in piece work view

    const segs = [];
    let cur = null;

    for (const r of data ?? []) {
      const builder = r?.builder || '';
      const neighborhood = r?.neighborhood || '';
      const key = `${builder}||${neighborhood}`;

      if (!cur || cur.key !== key) {
        if (cur) segs.push(cur);
        cur = {
          key,
          builder,
          neighborhood,
          rows: [],
          builderNotes: new Set(),
          neighborhoodNotes: new Set(),
          districtNotes: new Set(),
        };
      }
      cur.rows.push(r);
      if (r?.builder_note) cur.builderNotes.add(r.builder_note);
      if (r?.neighborhood_note) cur.neighborhoodNotes.add(r.neighborhood_note);
      if (r?.district_note) cur.districtNotes.add(r.district_note);
    }
    if (cur) segs.push(cur);

    return segs.map((s) => ({
      ...s,
      builderNote: Array.from(s.builderNotes).join(' | '),
      neighborhoodNote: Array.from(s.neighborhoodNotes).join(' | '),
      districtNote: Array.from(s.districtNotes).join(' | '),
    }));
  }, [data, isPieceWork]);

  const insuranceCharge = (insuranceRate != null && totalCost)
    ? totalCost * Number(insuranceRate)
    : 0;
  const finalTotalCost = totalCost - insuranceCharge;

  const formatExpiringDate = (dateVal) => {
    if (!dateVal) return null;
    const dt = (typeof dateVal === 'string') ? new Date(dateVal) : dateVal;
    if (isNaN(dt)) return null;
    const urgent = differenceInCalendarDays(dt, new Date()) <= 31; // past or within 31 days
    return { text: format(dt, 'MM/dd/yyyy'), urgent };
  };

  const formattedGl = formatExpiringDate(glExpDate);
  const formattedWc = formatExpiringDate(wcExpDate);

  return (
    <div id="ticket-print-root" className={classes.fullScreen}>
      {/* HEADER (unchanged, always shown) */}
      <div className={classes.headerBar}>
        <div className={classes.titleWrap}>
          <div>
            {!isPieceWork && <h2 style={{ margin: 0 }}>{teamName}</h2>}

            {isPieceWork && (
              <>
                <h2 style={{ margin: 0 }}>{employeeName}</h2>
                {/* <h4 style={{ margin: 0 }}>{teamName}</h4> */}
              </>
            )}
            <h4 style={{ margin: '5px 0', color: '#555' }}>{plumbers}</h4>
            <h4 style={{ margin: '5px', color: '#555' }}>
              {startDate
                ? (() => {
                  const [y, m, d] = startDate.split('-');
                  return `${m}/${d}/${y.slice(-2)}`;
                })()
                : ''}
              {endDate && isPieceWork
                ? ` - ${(() => {
                  const [y, m, d] = endDate.split('-');
                  return `${m}/${d}/${y.slice(-2)}`;
                })()}`
                : ''}

             
            </h4>
            {isPieceWork && (
              <>
              <h4 style={{ margin: '5px' }}>
                 {isPieceWork && (formattedGl || formattedWc) && (
                <>
                  
                  {formattedGl && (
                    <>
                      {" "}
                      <span style={{ color: formattedGl.urgent ? "red" : "inherit" }}>
                        GL Exp: {formattedGl.text}
                      </span>
                    </>
                  )}
                  {formattedGl && formattedWc && " · "}
                  {formattedWc && (
                    <>
                      {" "}
                      <span style={{ color: formattedWc.urgent ? "red" : "inherit" }}>
                        WC Exp: {formattedWc.text}
                      </span>
                    </>
                  )}
                </>
              )}
              </h4>
                <h4 style={{ margin: '5px' }}>

                  {/* {hourlyRate != null && (
                    <>*Hourly: {CentralCalls.formatTotal(hourlyRate)} · </>
                  )} */}
                  Total Cost: {CentralCalls.formatTotal(totalCost)}
                  {insuranceRate != null && (
                    <>
                      {' '}· Insurance: {CentralCalls.formatTotal(insuranceCharge)} ({(Number(insuranceRate) <= 1 ? (Number(insuranceRate) * 100).toFixed(2) : Number(insuranceRate)).toString()}%)
                    </>
                  )}
                  {' '}·Total Cost: {CentralCalls.formatTotal(finalTotalCost)}

                </h4>
                <h4 style={{ margin: '5px' }}>
                  Hours: {(totalHours != null && Number(totalHours) !== 0)
                    ? Number(totalHours).toFixed(2)
                    : Number(data.reduce((sum, row) => sum + (Number(row.hours) || 0), 0)).toFixed(2)}
                </h4>
              </>
            )}
          </div>
          <IconButton className={classes.screenOnly} color="primary" onClick={handlePrint}>
            <FontAwesomeIcon icon={faPrint} />
          </IconButton>
        </div>
      </div>

      {/* TABLE */}
      <Table
        className={classes.table}
        size="small"
        sx={{
          '& .MuiTableCell-body': { fontSize: '11px', lineHeight: 1.8 },
          '& .MuiTableCell-head': { fontWeight: 700, py: 0.5 },
          '& .MuiTableRow-root': { height: 'auto' },
        }}
      >
        {isPieceWork ? (
          <>
            <TableHead>
              <TableRow>
                {/* <TableCell className={classes.tableHeaderCell}>Plumber</TableCell> */}
                <TableCell className={classes.tableHeaderCell}>Builder</TableCell>
                <TableCell className={classes.tableHeaderCell}>Neighborhood</TableCell>
                <TableCell className={classes.tableHeaderCell}>Lot</TableCell>
                <TableCell className={classes.tableHeaderCell}>Completed</TableCell>
                <TableCell className={classes.tableHeaderCell}>Phase Type</TableCell>
                <TableCell className={classes.tableHeaderCell}>Comment</TableCell>
                <TableCell className={classes.tableHeaderCell}>Hours</TableCell>
                <TableCell className={classes.tableHeaderCell}>Cost</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(data || []).slice().sort((a, b) => new Date(a.date_complete) - new Date(b.date_complete)).map((row, idx) => (
                <TableRow key={(row.phase_status_oid ?? row.phase_oid ?? idx) + '::pw'} className={classes.row}>
                  {/* <TableCell className={classes.tableCell}>{getPlumberNames(row)}</TableCell> */}
                  <TableCell className={classes.tableCell}>{row.builder}</TableCell>
                  <TableCell className={classes.tableCell}>{row.neighborhood}</TableCell>
                  <TableCell className={classes.tableCell}>{row.lot_num}</TableCell>
                  <TableCell className={classes.tableCell}>{renderDate(row.date_complete)}</TableCell>
                  <TableCell className={classes.tableCell}><span style={{ fontWeight: 'bold' }}>{row.is_punch === 1 ? ' * Punch -' : ''}</span> {row.phase_type}</TableCell>
                  <TableCell className={classes.tableCell}>
                    {/* {row.punch_comments ? `Punch: ${row.punch_comments}. ` : ''} */}
                    {row.comments}
                  </TableCell>
                  <TableCell className={classes.tableCell}>{row.hours}</TableCell>
                  <TableCell className={classes.tableCell}>{formatCurrency(row.cost)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </>
        ) : (
          // ORIGINAL non-piece-work grouped layout (kept as-is)
          <TableBody>
            {segments.map((g, gi) => (
              <React.Fragment key={g.key + '::' + gi}>
                <TableRow className={`${classes.row} ${classes.groupHeaderRow}`}>
                  <TableCell colSpan={5} className={classes.tableCell} style={{ borderBottom: 'solid 2px black' }}>
                    <div className={classes.groupHeaderText}>
                      Builder: {g.builder} &nbsp; | &nbsp; Neighborhood: {g.neighborhood}
                    </div>
                    {(g.builderNote || g.neighborhoodNote) && (
                      <div>
                        {g.builderNote && (
                          <>
                            <span className={classes.notesEmphasis}>Builder Note:</span> {g.builderNote}
                          </>
                        )}
                        {g.builderNote && g.neighborhoodNote && '  |  '}
                        {g.neighborhoodNote && (
                          <>
                            <span className={classes.notesEmphasis}>Neighborhood Note:</span> {g.neighborhoodNote}
                          </>
                        )}
                        {(g.builderNote || g.neighborhoodNote) && '  |  '}
                      </div>
                    )}
                  </TableCell>
                </TableRow>

                {g.rows.map((row, idx) => (
                  <React.Fragment key={(row.phase_status_oid ?? row.phase_oid ?? idx) + '::row'}>
                    <TableRow className={classes.row}>
                      <TableCell size="small" variant="head" style={{ fontWeight: 'bold' }} className={classes.tableHeaderCell}>
                        Lot
                      </TableCell>
                      <TableCell variant="head" style={{ fontWeight: 'bold' }} className={classes.tableHeaderCell}>
                        Sewer
                      </TableCell>
                      <TableCell variant="head" style={{ fontWeight: 'bold' }} className={classes.tableHeaderCell}>
                        Phase
                      </TableCell>
                      <TableCell variant="head" style={{ fontWeight: 'bold' }} className={classes.tableHeaderCell}>
                        Hours
                      </TableCell>
                      <TableCell variant="head" style={{ fontWeight: 'bold' }} className={classes.tableHeaderCell}>
                        Fixtures
                      </TableCell>
                    </TableRow>

                    <TableRow className={classes.row}>
                      <TableCell className={classes.tableCell}>
                        {row.lot_num} - {row.address}
                      </TableCell>
                      <TableCell className={classes.tableCell}>{row.sewer_location}</TableCell>
                      <TableCell className={classes.tableCell}>
                        <span style={{ fontWeight: 'bold' }}>{row.is_punch === 1 ? ' * Punch -' : ''}</span> {row.phase_type}
                      </TableCell>
                      <TableCell className={classes.tableCell}>{row.hours}</TableCell>
                      <TableCell className={classes.tableCell}>{row.fixtures}</TableCell>
                    </TableRow>

                    <TableRow className={classes.row}>
                      <TableCell colSpan={5} className={classes.tableCell}>
                        <span style={{ fontWeight: 700 }}>Comments: </span>
                        {row.comments}
                      </TableCell>
                    </TableRow>

                    {g.districtNote && row.phase_type_id === 'underground' && (
                      <TableRow className={classes.row}>
                        <TableCell colSpan={5} className={classes.tableCell}>
                          <span className={classes.notesEmphasis}>Sewer Inspection Requirements:</span> {g.districtNote}
                        </TableCell>
                      </TableRow>
                    )}

                    <TableRow className={classes.row}>
                      <TableCell colSpan={5} className={classes.tableCell} style={{ borderBottom: 'solid 2px black' }}>
                        <span style={{ fontWeight: 700 }}>Result:</span>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </React.Fragment>
            ))}
          </TableBody>
        )}
      </Table>
    </div>
  );
}

export default TicketPrintOut;
